import {
  BookOpen,
  Search,
  FileText,
  Zap,
  Flame,
  TrendingUp,
  Star,
  Coins,
  Award,
  Heart,
  Repeat2,
  ArrowLeftRight,
  Settings,
  SlidersHorizontal,
  ListOrdered,
} from "lucide-react";
import "../styles/about.css";

const GAME_MODES = [
  {
    icon: <Search size={18} />,
    colour: "blue",
    name: "Spot the Word",
    desc: "Find target words hidden inside a passage before the clock runs out.",
  },
  {
    icon: <FileText size={18} />,
    colour: "indigo",
    name: "MCQ Quiz",
    desc: "Answer 10 multiple-choice comprehension questions in a timed session.",
  },
  {
    icon: <BookOpen size={18} />,
    colour: "violet",
    name: "Comprehension",
    desc: "Read a passage and answer both MCQ and written questions with mark schemes.",
  },
  {
    icon: <BookOpen size={18} />,
    colour: "violet",
    name: "Book Comprehension",
    desc: "Work through chapters of classic books with AI-generated question sets.",
  },
  {
    icon: <Star size={18} />,
    colour: "green",
    name: "Vocab Matching",
    desc: "Match words to their definitions to build your vocabulary.",
  },
  {
    icon: <Repeat2 size={18} />,
    colour: "teal",
    name: "Synonyms",
    desc: "Pick the word that means the same as the target word — difficulty scales with your ELO.",
  },
  {
    icon: <ArrowLeftRight size={18} />,
    colour: "orange",
    name: "Antonyms",
    desc: "Pick the word that means the opposite of the target word — a tougher twist on Synonyms.",
  },
  {
    icon: <Flame size={18} />,
    colour: "red",
    name: "Ranked Mode",
    desc: "Compete for ELO rating against time-limited rounds to climb the ranks.",
  },
];

export default function About() {
  return (
    <div className="about-page">
      <div className="about-container">

        {/* ── Hero ── */}
        <div className="about-hero">
          <div className="about-hero-icon">
            <BookOpen size={38} color="#fff" />
          </div>
          <h1>Owllet Reading Quest</h1>
          <p className="about-hero-tagline">
            An AI-powered reading and comprehension platform designed to make
            practising English skills engaging, rewarding, and genuinely fun.
          </p>
        </div>

        {/* ── What is it ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <BookOpen size={20} /> What is Owllet Reading Quest?
          </h2>
          <p>
            Owllet Reading Quest is an interactive learning platform built for
            students who want to sharpen their reading comprehension, vocabulary,
            and analytical writing skills. Every game mode is timed, scored, and
            tracked — so you can see real progress over time.
          </p>
          <p>
            Questions are generated on-the-fly by AI, which means you'll always
            face fresh challenges rather than the same recycled question bank.
            Written answers are marked automatically against AI-generated mark
            schemes, giving you instant, detailed feedback.
          </p>
        </div>

        {/* ── Game modes ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <TrendingUp size={20} /> Game Modes
          </h2>
          <p>There are eight ways to play, each targeting a different skill:</p>
          <div className="about-modes-grid">
            {GAME_MODES.map((m) => (
              <div key={m.name} className="about-mode-card">
                <div className={`about-mode-icon ${m.colour}`}>{m.icon}</div>
                <div>
                  <p className="about-mode-name">{m.name}</p>
                  <p className="about-mode-desc">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Rewards ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <Award size={20} /> Rewards &amp; Progression
          </h2>
          <p>
            Every game you complete earns rewards that track your growth and
            keep things exciting:
          </p>
          <div className="about-rewards-row">
            <span className="about-reward-pill xp">
              <Zap size={13} /> XP &amp; Levels
            </span>
            <span className="about-reward-pill coins">
              <Coins size={13} /> Coins
            </span>
            <span className="about-reward-pill owls">
              <Heart size={13} /> Owl Collection
            </span>
            <span className="about-reward-pill ranked">
              <Flame size={13} /> ELO Ranking
            </span>
          </div>
          <p style={{ marginTop: 14 }}>
            Coins can be spent in the <strong>Owl Gacha</strong> to collect
            unique owl companions for your Owl Nest. Climb the ELO ladder in
            Ranked Mode to earn titles from <em>Novice</em> all the way to{" "}
            <em>Legend</em>. Your full history — streaks, high scores, and
            performance trends — is available in the Statistics page.
          </p>
        </div>

        {/* ── Book Comprehension ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <BookOpen size={20} /> Book Comprehension
          </h2>
          <p>
            The Book Comprehension mode lets you work through chapters of classic
            literature. Choose a genre, pick a book, and select a chapter — then
            decide whether you want a timed MCQ test (20 questions, 20 minutes)
            or a written-answer exam (20 questions worth up to 70 marks, 45
            minutes).
          </p>
          <p>
            Question sets are generated once by AI and then cached, so subsequent
            attempts load instantly and stay consistent across sessions.
          </p>
        </div>

        {/* ── Vocabulary games ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <Repeat2 size={20} /> Synonyms &amp; Antonyms
          </h2>
          <p>
            Two dedicated vocabulary games let you practise word relationships at
            your own pace. <strong>Synonyms</strong> asks you to pick the word
            that means the same as the target; <strong>Antonyms</strong> asks for
            the opposite. Both games use a four-option multiple-choice format with
            a speaker button so you can hear the question read aloud.
          </p>
          <p>
            Each game tracks its own independent ELO rating, so your progress in
            one does not affect the other. Questions are drawn from words at your
            current ELO tier and enriched by AI in the background as you play —
            the pool grows automatically the more you use it.
          </p>
        </div>

        {/* ── Preferences ── */}
        <div className="about-section">
          <h2 className="about-section-heading">
            <Settings size={20} /> Preferences &amp; Personalisation
          </h2>
          <p>
            The <strong>⚙ Settings</strong> icon in the navigation bar opens the
            Preferences panel, where you can personalise two key aspects of your
            experience without affecting other players.
          </p>
          <div className="about-pref-grid">
            <div className="about-pref-card">
              <div className="about-pref-card-header">
                <ListOrdered size={16} />
                <span>Default Difficulty Tier</span>
              </div>
              <p>
                Choose a difficulty tier (1–21) that is automatically pre-selected on
                every game setup screen. Tier 1 is the easiest; Tier 21 is A-Level.
                Under-16 accounts can select up to Tier 17. You can still override the
                preset on any individual game setup screen.
              </p>
            </div>
            <div className="about-pref-card">
              <div className="about-pref-card-header">
                <SlidersHorizontal size={16} />
                <span>Vocabulary ELO</span>
              </div>
              <p>
                Three sliders (0–250) let you manually set the ELO for Synonyms,
                Antonyms, and Vocab Matching. ELO controls which words are shown —
                higher ELO means harder vocabulary. A warning is shown before applying
                any ELO change because it immediately affects word complexity.
              </p>
            </div>
          </div>
          <p style={{ marginTop: 4 }}>
            Your <strong>Owllet ID</strong> is also displayed at the top of the
            Preferences panel for quick reference — useful if you ever need to contact
            support or identify your account.
          </p>
        </div>

      </div>
    </div>
  );
}
